import { useState, useCallback, useRef } from "react";
import { KEYBOARD_SHORTCUT_MAP } from "@/shared/constants/keyboard-shortcut-map";
import { useKeyboardShortcut } from "@/shared/hooks/use-keyboard-shortcuts";
import { useOverlayStack } from "@/shared/hooks/use-overlay-stack";
import { useFocusTrap } from "@/shared/hooks/use-focus-trap";
import type { ShortcutRegistryEntry } from "@/shared/types/keyboard-shortcuts";

const CATEGORIES = ["Global", "Navigation", "Overlays", "Components"] as const;

function groupByCategory(
  shortcuts: ShortcutRegistryEntry[],
): Record<string, ShortcutRegistryEntry[]> {
  return shortcuts.reduce(
    (acc, s) => {
      (acc[s.category] ??= []).push(s);
      return acc;
    },
    {} as Record<string, ShortcutRegistryEntry[]>,
  );
}

export function KeyboardShortcutDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Register ? shortcut
  useKeyboardShortcut({
    id: "show-shortcuts",
    key: "?",
    description: "Show keyboard shortcuts",
    category: "Global",
    handler: open,
    suppressInInputs: true,
  });

  // Register with overlay stack for Escape handling & focus restoration
  useOverlayStack({
    id: "keyboard-shortcut-dialog",
    isOpen,
    onClose: close,
    level: 1,
  });

  // Focus trap
  const containerRef = useFocusTrap(isOpen);

  // Close on backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === backdropRef.current) {
        close();
      }
    },
    [close],
  );

  const grouped = groupByCategory(KEYBOARD_SHORTCUT_MAP);

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts reference"
        className="glass-card-elevated max-w-lg w-full mx-4 rounded-xl shadow-2xl overflow-hidden"
        data-overlay="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            Keyboard Shortcuts
          </h2>
          <button
            onClick={close}
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors p-1 rounded"
            aria-label="Close keyboard shortcuts dialog"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </div>
        <div className="space-y-6 max-h-[60vh] overflow-y-auto px-6 py-4">
          {CATEGORIES.map((category) => {
            const shortcuts = grouped[category];
            if (!shortcuts?.length) return null;
            return (
              <section key={category} aria-label={`${category} shortcuts`}>
                <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-2">
                  {category}
                </h3>
                <dl className="space-y-1">
                  {shortcuts.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between py-1.5"
                    >
                      <dt className="text-sm text-[var(--text-primary)]">
                        {s.description}
                      </dt>
                      <dd>
                        <kbd className="inline-flex items-center rounded border border-[var(--border-subtle)] bg-[var(--surface-glass,rgba(255,255,255,0.05))] px-2 py-0.5 text-xs font-mono text-[var(--text-secondary)]">
                          {s.display}
                        </kbd>
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
